import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  PhotographIcon,
  XIcon,
  StarIcon,
  UploadIcon,
  TrashIcon,
  RefreshIcon,
  PlayIcon,
} from "@heroicons/react/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/solid";
import toast from "react-hot-toast";

const MediaUpload = ({
  media = [],
  onChange,
  onUpload,
  onDelete,
  maxFiles = 10,
  maxSize = 5 * 1024 * 1024, // 5MB
  acceptedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/jpg",
    "video/mp4",
    "video/webm",
  ],
  loading = false,
  disabled = false,
  className = "",
  pendingRemovals = [],
  onPendingRemovalToggle,
  mode = "add", // add or edit
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-set primary media if none exists (only on initial load)
  useEffect(() => {
    if (media.length > 0 && mode === "add") {
      const activeMedia = media.filter(
        (item) => !pendingRemovals.includes(item.id || item._id)
      );

      const hasHero = activeMedia.some((item) => item.isHero);

      if (!hasHero && activeMedia.length > 0) {
        // Set first active media as hero (only images can be hero)
        const firstImage = activeMedia.find((item) => item.type !== "video");
        if (firstImage) {
          const updatedMedia = media.map((item) => ({
            ...item,
            isHero: (item.id || item._id) === (firstImage.id || firstImage._id),
          }));
          onChange(updatedMedia);
        }
      }
    }
  }, []); // Empty dependency to run only once on mount

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (
        disabled ||
        !e.dataTransfer.files ||
        e.dataTransfer.files.length === 0
      ) {
        return;
      }

      const files = Array.from(e.dataTransfer.files);
      await handleFiles(files);
    },
    [disabled]
  );

  // Handle file selection
  const handleFileSelect = async (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    const files = Array.from(e.target.files);
    await handleFiles(files);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Process selected files
  const handleFiles = async (files) => {
    // Count active media (excluding pending removals)
    const activeMediaCount = media.filter(
      (item) => !pendingRemovals?.includes(item.id || item._id)
    ).length;

    // Validate number of files
    if (activeMediaCount + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate file types and sizes
    const validFiles = [];
    for (const file of files) {
      // Check if file type is accepted
      const isAcceptedType = acceptedTypes.some((type) => {
        if (type.includes("*")) {
          // Handle wildcard types like image/* or video/*
          const [category] = type.split("/");
          return file.type.startsWith(category + "/");
        }
        return file.type === type;
      });

      if (!isAcceptedType) {
        toast.error(`${file.name} is not a valid file type`);
        continue;
      }

      if (file.size > maxSize) {
        toast.error(
          `${file.name} exceeds maximum size of ${maxSize / (1024 * 1024)}MB`
        );
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      return;
    }

    // Check if there's a primary media already
    const hasHero = media.some(
      (m) =>
        m.isHero &&
        !pendingRemovals?.includes(m.id || m._id) &&
        m.type !== "video"
    );

    // Upload files or create local previews
    if (onUpload) {
      setUploading(true);
      try {
        await onUpload(validFiles);

        // Create preview items for the new files
        const newMedia = [...media];

        validFiles.forEach((file, index) => {
          const isVideo = file.type.startsWith("video/");
          const uniqueId = `new-${Date.now()}-${index}-${Math.random()}`;

          newMedia.push({
            id: uniqueId,
            url: URL.createObjectURL(file),
            file: file,
            type: isVideo ? "video" : "image",
            isNew: true,
            isHero: !hasHero && index === 0 && !isVideo,
            order: media.length + index,
          });
        });

        onChange(newMedia);
        toast.success(`${validFiles.length} file(s) added successfully`);
      } catch (error) {
        toast.error(error.message || "Failed to add files");
      } finally {
        setUploading(false);
      }
    }
  };

  // Set hero image - Fixed to properly update
  const handleSetHero = (mediaId) => {
    const mediaItem = media.find((item) => (item.id || item._id) === mediaId);

    // Don't allow videos to be hero
    if (mediaItem?.type === "video") {
      toast.error("Videos cannot be set as primary media");
      return;
    }

    const updatedMedia = media.map((item) => {
      const itemId = item.id || item._id;
      return {
        ...item,
        isHero: itemId === mediaId,
      };
    });

    onChange(updatedMedia);
    toast.success("Primary media updated");
  };

  // Delete or mark for deletion - Fixed
  const handleDeleteImage = async (mediaId) => {
    const mediaItem = media.find((item) => (item.id || item._id) === mediaId);

    if (!mediaItem) {
      console.error("Media item not found:", mediaId);
      return;
    }

    // If it's a new file (not yet saved), remove immediately
    if (mediaItem.isNew) {
      // Call onDelete to handle file removal from parent state
      if (onDelete) {
        try {
          await onDelete(mediaId);

          // Update local media array
          const updatedMedia = media.filter(
            (item) => (item.id || item._id) !== mediaId
          );

          // If deleted was hero, set first remaining image as hero
          if (mediaItem.isHero && updatedMedia.length > 0) {
            const firstActiveImage = updatedMedia.find(
              (m) =>
                !pendingRemovals?.includes(m.id || m._id) && m.type !== "video"
            );
            if (firstActiveImage) {
              firstActiveImage.isHero = true;
            }
          }

          onChange(updatedMedia);
          toast.success("File removed");
        } catch (error) {
          console.error("Delete error:", error);
          toast.error("Failed to remove file");
        }
      }
    } else if (mode === "edit" && onPendingRemovalToggle) {
      // For existing media in edit mode, toggle pending removal
      onPendingRemovalToggle(mediaId);

      // If this was the hero and it's being marked for removal, set a new hero
      if (mediaItem.isHero && !pendingRemovals.includes(mediaId)) {
        const remainingMedia = media.filter((item) => {
          const itemId = item.id || item._id;
          return (
            itemId !== mediaId &&
            !pendingRemovals.includes(itemId) &&
            item.type !== "video"
          );
        });

        if (remainingMedia.length > 0) {
          const updatedMedia = media.map((item) => {
            const itemId = item.id || item._id;
            if (
              itemId === remainingMedia[0].id ||
              itemId === remainingMedia[0]._id
            ) {
              return { ...item, isHero: true };
            }
            if (itemId === mediaId) {
              return { ...item, isHero: false };
            }
            return item;
          });
          onChange(updatedMedia);
        }
      }
    } else if (onDelete) {
      // Fallback to immediate deletion if no pending removal handler
      try {
        await onDelete(mediaId);
        const updatedMedia = media.filter(
          (item) => (item.id || item._id) !== mediaId
        );

        if (mediaItem.isHero && updatedMedia.length > 0) {
          const firstImage = updatedMedia.find((m) => m.type !== "video");
          if (firstImage) {
            firstImage.isHero = true;
          }
        }

        onChange(updatedMedia);
        toast.success("Media deleted successfully");
      } catch (error) {
        console.error("Delete error:", error);
        toast.error(error.message || "Failed to delete media");
      }
    }
  };

  // Get media ID consistently
  const getMediaId = (item) => {
    return item.id || item._id || `temp-${Date.now()}`;
  };

  // Check if media is marked for removal
  const isMarkedForRemoval = (item) => {
    const id = getMediaId(item);
    return pendingRemovals?.includes(id);
  };

  // Render video element with proper fallback
  const renderVideo = (item, index) => {
    return (
      <div className="relative w-full h-full">
        <video
          className="w-full h-full object-cover"
          muted
          playsInline
          onError={(e) => {
            console.error("Video error:", e);
            // Set a fallback poster or show error state
            e.target.style.display = "none";
            const fallback = e.target.nextElementSibling;
            if (fallback) fallback.style.display = "flex";
          }}
        >
          <source
            src={item.url || item.thumbnail}
            type={item.file?.type || "video/mp4"}
          />
          Your browser does not support the video tag.
        </video>
        {/* Fallback display */}
        <div
          className="absolute inset-0 bg-gray-200 items-center justify-center hidden"
          style={{ display: "none" }}
        >
          <div className="text-center">
            <PlayIcon className="w-12 h-12 text-gray-500 mx-auto mb-2" />
            <p className="text-xs text-gray-500">Video Preview</p>
          </div>
        </div>
        {/* Play icon overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-black bg-opacity-50 rounded-full p-3">
            <PlayIcon className="w-8 h-8 text-white" />
          </div>
        </div>
      </div>
    );
  };

  // Calculate active media count
  const activeMediaCount = media.filter((m) => !isMarkedForRemoval(m)).length;
  const pendingRemovalCount = pendingRemovals?.length || 0;

  return (
    <div className={className}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-all ${
          dragActive
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-gray-400"
        } ${
          disabled || activeMediaCount >= maxFiles
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer"
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() =>
          !disabled &&
          !uploading &&
          activeMediaCount < maxFiles &&
          fileInputRef.current?.click()
        }
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(",")}
          onChange={handleFileSelect}
          disabled={disabled || uploading || activeMediaCount >= maxFiles}
          className="hidden"
        />

        <div className="text-center">
          {uploading ? (
            <>
              <RefreshIcon className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
              <p className="mt-2 text-sm text-gray-600">Uploading...</p>
            </>
          ) : activeMediaCount >= maxFiles ? (
            <>
              <UploadIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">
                Maximum files limit reached ({maxFiles} files)
              </p>
              {pendingRemovalCount > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Remove existing files to add new ones
                </p>
              )}
            </>
          ) : (
            <>
              <UploadIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                {dragActive
                  ? "Drop files here"
                  : "Click or drag files to upload"}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {`Supported: Images (JPEG, PNG, WebP) & Videos (MP4, WebM) • Max ${
                  maxSize / (1024 * 1024)
                }MB • Up to ${maxFiles} files`}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Media Gallery */}
      {media.length > 0 && (
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Uploaded Media ({activeMediaCount}/{maxFiles})
              {pendingRemovalCount > 0 && (
                <span className="text-red-600 ml-2">
                  ({pendingRemovalCount} marked for removal)
                </span>
              )}
            </h4>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {media.map((item, index) => {
              const mediaId = getMediaId(item);
              const isPendingRemoval = isMarkedForRemoval(item);
              const isVideo = item.type === "video";

              return (
                <div
                  key={mediaId}
                  data-file-id={mediaId}
                  className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
                    item.isHero && !isPendingRemoval
                      ? "ring-2 ring-yellow-400 border-yellow-400"
                      : "border-gray-200"
                  } ${isPendingRemoval ? "opacity-50 border-red-500" : ""}`}
                >
                  {/* Media Display */}
                  <div className="aspect-square bg-gray-100 relative">
                    {isVideo ? (
                      renderVideo(item, index)
                    ) : (
                      <img
                        src={item.url || item.thumbnail}
                        alt={item.name || `Media ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='50' font-family='Arial' font-size='12' fill='%239ca3af' text-anchor='middle' alignment-baseline='middle'%3ENo Preview%3C/text%3E%3C/svg%3E";
                        }}
                      />
                    )}
                  </div>

                  {/* Pending Removal Overlay */}
                  {isPendingRemoval && (
                    <div className="absolute inset-0 bg-red-500 bg-opacity-20 flex items-center justify-center">
                      <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-medium">
                        Will be removed
                      </span>
                    </div>
                  )}

                  {/* Overlay Actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200">
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex gap-2">
                        {/* Set as Hero - only for images */}
                        {!isPendingRemoval && !isVideo && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetHero(mediaId);
                            }}
                            className="p-2 bg-white rounded-lg shadow-lg hover:bg-yellow-50"
                            title={
                              item.isHero ? "Primary Media" : "Set as Primary"
                            }
                          >
                            {item.isHero ? (
                              <StarIconSolid className="w-5 h-5 text-yellow-500" />
                            ) : (
                              <StarIcon className="w-5 h-5 text-gray-600" />
                            )}
                          </button>
                        )}

                        {/* Delete/Restore */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(mediaId);
                          }}
                          className={`p-2 bg-white rounded-lg shadow-lg ${
                            isPendingRemoval
                              ? "hover:bg-green-50"
                              : "hover:bg-red-50"
                          }`}
                          title={
                            isPendingRemoval
                              ? "Undo Removal"
                              : item.isNew
                              ? "Remove Media"
                              : "Mark for Removal"
                          }
                        >
                          {isPendingRemoval ? (
                            <RefreshIcon className="w-5 h-5 text-green-600" />
                          ) : (
                            <TrashIcon className="w-5 h-5 text-red-600" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Hero Badge */}
                  {item.isHero && !isPendingRemoval && (
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                        Primary
                      </span>
                    </div>
                  )}

                  {/* Media Type Badge for Videos */}
                  {isVideo && (
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Video
                      </span>
                    </div>
                  )}

                  {/* Status Badge for existing media */}
                  {item.isExisting && !item.isNew && !isPendingRemoval && (
                    <div className="absolute bottom-2 left-2">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Existing
                      </span>
                    </div>
                  )}

                  {/* New Badge for new media */}
                  {item.isNew && !isPendingRemoval && (
                    <div className="absolute bottom-2 left-2">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        New
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
